import { useEffect, useMemo, useState } from 'react';
import { endOfDay, endOfMonth, isAfter, startOfMonth } from 'date-fns';
import { api } from 'services/api';
import { Guide } from 'types/guide';
import { Paginated } from 'types/paginated';
import { Surgerie } from 'types/surgerie';
import { buildRanking, buildTimeline } from '../dashboardMetrics';
import { ExecutiveDashboardPeriod } from '../ExecutiveDashboardFilterBox';

const currentMonthStart = startOfMonth(new Date());
const currentMonthEnd = endOfMonth(new Date());
const defaultPeriod: ExecutiveDashboardPeriod = {
  initialDate: currentMonthStart,
  finalDate: currentMonthEnd,
};

// Consultas e orçamentos do período aplicado, com os indicadores calculados.
export function useExecutiveDashboard() {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [surgeries, setSurgeries] = useState<Surgerie[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showValues, setShowValues] = useState(true);
  const [period, setPeriod] = useState(defaultPeriod);
  const [appliedPeriod, setAppliedPeriod] = useState(defaultPeriod);
  const [periodError, setPeriodError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function fetchDashboardData() {
      setLoading(true);
      setHasError(false);

      try {
        if (!appliedPeriod.initialDate || !appliedPeriod.finalDate) return;

        const initialDate = appliedPeriod.initialDate;
        const finalDate = endOfDay(appliedPeriod.finalDate);

        const [guidesResponse, surgeriesResponse] = await Promise.all([
          api.get<Paginated<Guide[]>>('/guides', {
            params: {
              initial_date: initialDate,
              final_date: finalDate,
              date_filter: 'created_at',
              page: 1,
              rows: 96,
            },
          }),
          api.get<Surgerie[]>('/budgets/all', {
            params: {
              initial_date: initialDate,
              final_date: finalDate,
              date_filter: 'created_at',
            },
          }),
        ]);

        if (!isMounted) return;

        setGuides(guidesResponse.data.items.filter(guide => guide.status !== 'deleted'));
        setSurgeries(surgeriesResponse.data.filter(surgerie => !surgerie.deleted_at));
      } catch {
        if (isMounted) setHasError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [appliedPeriod]);

  const closedSurgeries = useMemo(() => surgeries.filter(surgerie => surgerie.status === 'closed'), [surgeries]);
  const openSurgeries = useMemo(() => surgeries.filter(surgerie => surgerie.status === 'open'), [surgeries]);

  const guideRevenue = useMemo(
    () => guides.reduce((total, guide) => total + Number(guide.value_total || 0), 0),
    [guides],
  );

  const soldRevenue = useMemo(
    () => closedSurgeries.reduce((total, surgerie) => total + Number(surgerie.value_total || 0), 0),
    [closedSurgeries],
  );

  const openPipeline = useMemo(
    () => openSurgeries.reduce((total, surgerie) => total + Number(surgerie.value_total || 0), 0),
    [openSurgeries],
  );

  const conversionRate = surgeries.length ? (closedSurgeries.length / surgeries.length) * 100 : 0;
  const averageTicket = closedSurgeries.length ? soldRevenue / closedSurgeries.length : 0;
  const averageGuide = guides.length ? guideRevenue / guides.length : 0;
  const totalRevenue = guideRevenue + soldRevenue;

  const procedureRanking = useMemo(
    () =>
      buildRanking(
        closedSurgeries,
        surgerie => surgerie.procedure?.name || 'Procedimento não informado',
        surgerie => Number(surgerie.value_total || 0),
      ),
    [closedSurgeries],
  );

  const doctorRanking = useMemo(
    () =>
      buildRanking(
        [...closedSurgeries, ...guides],
        item => {
          if ('value_doctor' in item) return item.doctor?.doctor?.name || 'Médico não informado';

          return item.doctor?.name || 'Médico não informado';
        },
        item => Number(item.value_total || 0),
      ),
    [closedSurgeries, guides],
  );

  const timeline = useMemo(() => buildTimeline(guides, closedSurgeries), [guides, closedSurgeries]);
  const timelineMax = Math.max(...timeline.map(item => item.amount), 1);
  const rankingMax = Math.max(
    ...procedureRanking.map(item => item.amount || 0),
    ...doctorRanking.map(item => item.amount || 0),
    1,
  );
  const handlePeriodChange = (index: keyof ExecutiveDashboardPeriod, value: Date | null) => {
    setPeriod(currentPeriod => ({ ...currentPeriod, [index]: value }));
  };
  const handleSearchPeriod = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (!period.initialDate || !period.finalDate) {
      setPeriodError('Informe as datas inicial e final.');
      return;
    }

    if (isAfter(period.initialDate, period.finalDate)) {
      setPeriodError('A data inicial não pode ser maior que a data final.');
      return;
    }

    setPeriodError('');
    setAppliedPeriod(period);
  };

  return {
    guides,
    surgeries,
    closedSurgeries,
    openSurgeries,
    loading,
    hasError,
    period,
    periodError,
    handlePeriodChange,
    handleSearchPeriod,
    guideRevenue,
    soldRevenue,
    openPipeline,
    conversionRate,
    averageTicket,
    averageGuide,
    totalRevenue,
    procedureRanking,
    doctorRanking,
    timeline,
    timelineMax,
    rankingMax,
  };
}
